import { IQuillDrawer } from '@Common/models/redux/store';
import Track from 'core/logger/track';

class CommonTrack {
  multipleSessionsError = () => {
    Track.getInstance().ampEvent('concurrentSessionFlagShown');
    Track.getInstance().storeActionEvent('concurrentSessionFlagShown');
  };

  shownSignupModal = () => {
    Track.getInstance().shownSection('paraphraserLimitSignupModal');
  };

  clickedFileUploadButton = () => {
    Track.getInstance().ampEvent('clickedFileUploadButton');
    Track.getInstance().storeActionEvent('clickedFileUploadButton');
  };

  uploadedFile = (fileType: string, fileSize: string, success: boolean) => {
    Track.getInstance().ampEvent('uploadedFile', {
      fileType,
      fileSize,
      success,
    });
    Track.getInstance().storeActionEvent('uploadedFile', {
      fileType,
      fileSize,
      success,
    });
  };

  initiatedUrlScrapping = (url: string, success: boolean) => {
    Track.getInstance().ampEvent('initiatedUrlScrapping', { url, success });
    Track.getInstance().storeActionEvent('initiatedUrlScrapping', {
      url,
      success,
    });
  };

  youtubeScrapeStatus = (url: string, status: string, autoGenerated: boolean) => {
    const actionName = 'comn/user/pasted_YouTube_url';
    Track.getInstance().storeActionEvent(actionName, {
      url,
      status,
      autoGenerated,
    });
    Track.getInstance().ampEvent(actionName, { url, status, autoGenerated });
  };

  // TODO: make fileType enum.
  fileExported = (success: boolean) => {
    Track.getInstance().ampEvent('fileExported', { success, fileType: 'docx' });
    Track.getInstance().storeActionEvent('fileExported', {
      success,
      fileType: 'docx',
    });
  };

  clickedCompareMode = (open: boolean) => {
    Track.getInstance().ampEvent('pphr/user/toggledCompareModes', {
      isOpen: open,
    });
    Track.getInstance().storeActionEvent('pphr/user/toggledCompareModes', {
      isOpen: open,
    });
  };

  clickedSocialButtons = (mode: string) => {
    Track.getInstance().ampEvent(`pphr/user/clicked_${mode}_button`, {
      position: 'ParaphraseLimitSignupModal',
    });
    Track.getInstance().storeActionEvent(`pphr/user/clicked_${mode}_button`, {
      position: 'ParaphraseLimitSignupModal',
    });
  };

  clickedSignupModalLinks = (mode: string) => {
    Track.getInstance().ampEvent(`pphr/user/clicked_${mode}_link`, {
      position: 'ParaphraseLimitSignupModal',
    });
    Track.getInstance().storeActionEvent(`pphr/user/clicked_${mode}_link`, {
      position: 'ParaphraseLimitSignupModal',
    });
  };

  settingChanged = (change: { [key: string]: string | boolean }) => {
    Track.getInstance().storeActionEvent('settingChanged', change);
    Track.getInstance().ampEvent('settingChanged', change);
  };

  trackingDisabled = () => {
    Track.getInstance().storeActionEvent('comn/user/stopped_tracking');
    Track.getInstance().ampEvent('comn/user/stopped_tracking');
  };

  npsActions = (
    actionName: string,
    submittedInputCount?: number,
    premium?: boolean,
    value?: number,
    creationTime?: string
  ) => {
    Track.getInstance().storeActionEvent(actionName, {
      submittedInputCount,
      premium,
      value,
      creationTime,
    });
    Track.getInstance().ampEvent(actionName, {
      submittedInputCount,
      premium,
      value,
      creationTime,
    });
  };

  sideDrawerStateChanged = (state: 'opened' | 'closed', drawerName: IQuillDrawer, triggeredBy = 'sideControlBar') => {
    Track.getInstance().storeActionEvent('sideDrawerStateChanged', {
      state,
      drawerContent: drawerName,
      triggeredBy,
    });
    Track.getInstance().ampEvent('sideDrawerStateChanged', {
      state,
      drawerContent: drawerName,
      triggeredBy,
    });
  };

  sideBarStateChangeAction = (state: boolean, actionName: string, source: string) => {
    Track.getInstance().storeActionEvent(actionName, {
      state,
      source,
    });
    Track.getInstance().ampEvent(actionName, {
      state,
      source,
    });
  };

  premiumUpsaleAction = (actionName: string) => {
    Track.getInstance().storeActionEvent(actionName);
    Track.getInstance().ampEvent(actionName);
  };

  modalStateChangeAction = (state: string, actionName: string) => {
    Track.getInstance().storeActionEvent(actionName, {
      state,
    });
    Track.getInstance().ampEvent(actionName, {
      state,
    });
  };

  interactedCrossProductUpsellModal = (upsoldProduct: string, action: 'shown' | 'accepted' | 'closed') => {
    const actionName = 'comn/user/cross_product_upsell';
    Track.getInstance().storeActionEvent(actionName, {
      action,
      upsoldProduct,
    });
    Track.getInstance().ampEvent(actionName, {
      action,
      upsoldProduct,
    });
  };

  homePageBtnClick = (btnPosition: string) => {
    Track.getInstance().ampEvent('homePageLinkClicked', {
      position: btnPosition,
    });
  };

  // same function is being used in misc tracking
  extensionLinkClicked = (extensionName: 'word' | 'chrome' | 'docs', position: string) => {
    const eventName = 'clickedExtensionLink';
    const data = {
      extensionName,
      position,
    };
    Track.getInstance().storeActionEvent(eventName, data);
    Track.getInstance().ampEvent(eventName, data);
  };

  premiumTooltipsHovered = (duration: number, tooltip: string | undefined) => {
    Track.getInstance().ampEvent('premiumTooltipsHovered', {
      duration,
      tooltip,
    });
  };

  clickedExpertReviewUpload = (position: string) => {
    const eventName = 'comn/user/clicked_upload_to_scribbr';
    Track.getInstance().storeActionEvent(eventName, { position });
    Track.getInstance().ampEvent(eventName, { position });
  };

  userInteractedWithERModal = (action: string) => {
    const eventName = 'comn/user/interacted_expert_review_modal';
    Track.getInstance().storeActionEvent(eventName, { action });
    Track.getInstance().ampEvent(eventName, { action });
  };
  userInteractedWithInfluencerPageForm = (data: Object) => {
    const eventName = 'comn/user/form_submitted_influencer_page';
    Track.getInstance().storeActionEvent(eventName, { data });
    Track.getInstance().ampEvent(eventName);
  };

  replayRequestFailure = (data: { url: string; errorCode?: number; message?: string; trace?: string }) => {
    const eventName = 'comn/system/replay_request_failed';
    Track.getInstance().storeActionEvent(eventName, data);
    Track.getInstance().ampEvent(eventName, data);
  };

  clickedTrustPilotRedirect = () => {
    const eventName = 'comn/user/clicked_trustpilot_redirect';
    Track.getInstance().storeActionEvent(eventName);
    Track.getInstance().ampEvent(eventName);
  };

  calculatedPriceOnExpertReview = (price: number, planSelected: string, wordCount: any) => {
    const eventName = 'comn/user/calculated_expert_review_price';
    Track.getInstance().storeActionEvent(eventName, { planSelected, wordCount, price });
    Track.getInstance().ampEvent(eventName, { planSelected, wordCount, price });
  };
}

const commonTrack = new CommonTrack();
export default commonTrack;
